import { useEffect, useRef } from 'react';

type Props = {
  task: () => Promise<unknown>;
  delayMs?: number;
  enabled?: boolean;
};

export const usePollingTask = ({ task, delayMs, enabled = true }: Props) => {
  const taskRef = useRef(task);

  useEffect(() => {
    taskRef.current = task;
  }, [task]);

  useEffect(() => {
    if (!enabled || !delayMs || delayMs <= 0) return;

    let isStopped = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const scheduleNext = () => {
      if (isStopped) return;

      timeoutId = setTimeout(runTask, delayMs);
    };

    const runTask = () => {
      timeoutId = null;

      void Promise.resolve()
        .then(() => taskRef.current())
        .catch(() => undefined)
        .finally(scheduleNext);
    };

    scheduleNext();

    return () => {
      isStopped = true;

      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
  }, [enabled, delayMs]);
};
